import { capitalize } from '@mui/material';
import {
  type ConferenceFilter,
  type OrganizationRankingResponseRankingResponseRelatedPagedData,
  type PersonSportRankingResponseRelatedRankingResponseRelatedPagedData,
  type PositionResponse,
  rdbV1FiltersConferencesList,
  rdbV1FiltersYearsList,
  type StateResponse,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/hocs/withValidUrl';
import { proxyApi } from '@on3/ui-lib/index';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { slugify } from '@on3/ui-lib/utils/slugify';
import { PlayerRankingsPage } from '@public/components/Db/Rankings/PlayerRankings/PlayerRankingsPage';
import { TeamRankings } from '@public/components/Db/Rankings/Team/TeamRankings';
import { AdSidebarLayout } from '@public/components/Layouts/AdSidebarLayout';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { getFulfilledData } from '@public/utils/getFulfilledData';
import { getProxyPromiseData } from '@public/utils/getPromiseData';
import { FilterRoutes, RDBRoutes, type RouteName } from '@public/utils/routes';
import type { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import { type ParsedUrlQuery } from 'querystring';
import { useMemo } from 'react';

export enum AllowedRankingType {
  IndustryPlayer = 'industry-player',
  Player = 'player',
  IndustryTeam = 'industry-team',
  Team = 'team',
}

export interface IRankingsPageProps extends IGenericPageProps {
  rankingType: AllowedRankingType;
  playerData?: PersonSportRankingResponseRelatedRankingResponseRelatedPagedData;
  teamData?: OrganizationRankingResponseRankingResponseRelatedPagedData;
  filters: {
    conferences: ConferenceFilter[];
    positions: PositionResponse[];
    states: StateResponse[];
    years: number[];
  };
}

export interface DBRankingQuery extends ParsedUrlQuery {
  rankingType: AllowedRankingType;
  year?: string;
  sport?: string;
}

export const RankingsPage = ({
  rankingType,
  playerData,
  filters,
  teamData,
  ...props
}: IRankingsPageProps) => {
  const {
    query: { year, sport, position, state, sort, type },
  } = useRouter();
  const hasJuco = type?.includes('junior');
  const displaySport =
    sport && hasJuco ? `${sport} Junior College` : `${sport}`;
  const upperCaseSport = capitalize(`${displaySport}`).replace('-', ' ');

  const fullPositionName = useMemo(() => {
    const selectedPositionItem = filters?.positions?.find(
      (pos) => pos.abbreviation?.toLowerCase() === position,
    );

    if (selectedPositionItem?.key === -1) {
      return null;
    }

    return selectedPositionItem?.name;
  }, [filters?.positions, position]);

  const fullStateName = useMemo(() => {
    const selectedStateItem = filters?.states?.find(
      (st) => st.abbreviation?.toLowerCase() === state,
    );

    if (selectedStateItem?.key === -1) {
      return null;
    }

    return selectedStateItem?.name;
  }, [filters?.states, state]);

  const verbiageOptions: Record<
    AllowedRankingType,
    { header: string; description: string }
  > = {
    [AllowedRankingType.IndustryPlayer]: {
      header: `${year} Industry Top ${upperCaseSport}${
        (position && ` ${fullPositionName}`) || ''
      }${(state && ` ${fullStateName}`) || ''} Recruits`,
      description: `A list of the top ${year} ${upperCaseSport} recruits based off of a proprietary algorithm that compiles ratings and rankings from all 3 major recruiting media services, including Rivals.`,
    },
    [AllowedRankingType.Team]: {
      header: `${year} ${upperCaseSport} Team Recruiting Rankings`,
      description: `A list of the top ${year} ${upperCaseSport} teams in order of recruiting rankings solely based off of the Rivals Player Rankings`,
    },
    [AllowedRankingType.IndustryTeam]: {
      header: `${year} Industry ${upperCaseSport} Team Recruiting Rankings`,
      description: `A list of the top ${year} ${upperCaseSport} teams in order of recruiting rankings solely based off of the Rivals Industry Ranking`,
    },
    [AllowedRankingType.Player]: {
      header: `${year} Top ${upperCaseSport}${
        (position && ` ${fullPositionName}`) || ''
      }${(state && ` ${fullStateName}`) || ''} Recruits`,
      description: `A list of the top ${year} ${upperCaseSport} recruits as determined by Rivals.`,
    },
  };
  const { description, header } = verbiageOptions[rankingType || 'player'];
  const playerRankingsKey = `${year}-${sport}-${position}-${state}-${rankingType}-${type}-${sort}-${playerData?.pagination?.currentPage}`;
  const teamRankingsKey = `${year}-${sport}-${position}-${state}-${rankingType}-${sort}-${teamData?.pagination?.currentPage}`;

  const noIndex =
    playerData?.pagination?.count === 0 || teamData?.pagination?.count === 0;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={header}
    >
      {rankingType === AllowedRankingType.Player ||
      rankingType === AllowedRankingType.IndustryPlayer ? (
        <PlayerRankingsPage
          {...playerData}
          filters={filters}
          key={playerRankingsKey}
          title={header}
        />
      ) : (
        <AdSidebarLayout>
          <TeamRankings {...teamData} filters={filters} key={teamRankingsKey} />
        </AdSidebarLayout>
      )}
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const {
    rankingType,
    year,
    sport,
    page = '1',
  }: DBRankingQuery = ctx.query as DBRankingQuery;
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData || !year || year === '0') {
    return {
      notFound: true,
    };
  }

  const { rankingSports } = siteData || {};

  const incomingRankingType = String(ctx.query.rankingType);
  const isAllowed = Object.values(AllowedRankingType).includes(
    incomingRankingType as AllowedRankingType,
  );

  if (!isAllowed) {
    return {
      notFound: true,
    };
  }

  const rankType = incomingRankingType as AllowedRankingType;

  try {
    const { conference, state, position, sort, type } = ctx.query as {
      conference?: string;
      state?: string;
      position?: string;
      sort?: string;
      type?: string;
    };

    const sportObj = rankingSports?.find(
      (sp) => sp?.name && slugify(sp?.name) === sport,
    );

    if (!sportObj) {
      return {
        notFound: true,
      };
    }

    if (!sportObj.isIndustryRankable && rankingType.includes('industry')) {
      return {
        redirect: {
          permanent: true,
          destination: `/rivals/rankings/${
            rankingType.split('-')[1]
          }/${sport}/${year}/`,
        },
      };
    }

    const sportKey = sportObj?.key;

    const rankingTypeParam = rankType.includes('player')
      ? 'Player'
      : 'Organization';
    const isIndustry = rankType.includes('industry');
    const orderBy = isIndustry ? 'Consensus' : 'On3';
    const industryQuery = rankType === 'industry-team' ? 'consensus-' : '';
    const rankingTypeQuery = `${industryQuery}team-rankings` as const;

    if (rankingTypeParam === 'Player') {
      const personSportParams = {
        consensus: isIndustry,
        orderBy,
        sportKey,
        year,
        rankingType: rankingTypeParam,
        page,
        limit: 300,
        stateAbbr: state,
        positionAbbr: position,
        orgType: type || 'HighSchool',
      };
      const positionParams = {
        sportKey,
        positionType: 'Player',
      };
      const [playerData, years, positions, states] = await Promise.allSettled([
        getProxyPromiseData(RDBRoutes.personSportRankings, personSportParams),
        rdbV1FiltersYearsList(proxyApi),
        getProxyPromiseData(FilterRoutes.position, positionParams),
        getProxyPromiseData(FilterRoutes.state),
      ]);

      return {
        props: {
          pageType: 'database',
          playerData: getFulfilledData(playerData) || [],
          filters: {
            years: getFulfilledData(years) || [],
            sports: rankingSports,
            states: getFulfilledData(states) || [],
            positions: getFulfilledData(positions) || [],
          },
          siteData,
          userData: userData || {},
          rankingType: rankType,
        },
      };
    }

    const teamParams = {
      page: +page,
      conferenceAbbr: conference,
      sort,
      sport: sportKey,
      year: +year,
    };
    const [teamData, years, conferences] = await Promise.allSettled([
      getProxyPromiseData(
        RDBRoutes.teamRankings`${sport}${year}${rankingTypeQuery}` as RouteName,
        teamParams,
      ),
      rdbV1FiltersYearsList(proxyApi),
      rdbV1FiltersConferencesList(proxyApi, { sportKey }),
    ]);

    if (!getFulfilledData(teamData)) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageType: 'database',
        teamData: getFulfilledValue(teamData),
        filters: {
          years: getFulfilledValue(years),
          sports: rankingSports,
          conferences: getFulfilledValue(conferences),
        },
        siteData,
        userData: userData || {},
        rankingType: rankType,
      },
    };
  } catch (e) {
    return {
      props: {
        playerData: [],
        teamData: [],
        filters: {
          years: [],
          sports: [],
          states: [],
          positions: [],
          conferences: [],
        },
        siteData,
        userData: userData || {},
        rankingType: rankType,
      },
    };
  }
};

export default withValidUrl(RankingsPage);
